import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";

import apiService from "../../services/api.services";

// Types
//export type ResourceType = "DOC" | "VIDEO" | "IMAGE";
export type ResourceType = "DOC" | "VIDEO" | "IMAGE";

export type SubjectType =
  | "Mathematics"
  | "Physics"
  | "Chemistry"
  | "Biology"
  | "Literature"
  | "All";

export interface Resource {
  id: number;
  title: string;
  description: string;
  resourceType: ResourceType;
  uploadedBy: number;
  courseId?: number;
  tags?: string[];
  resourceAttachment?: any;
  subject?: SubjectType;
  createdAt: Date | string;
  isDeleted?: boolean;
}

export interface ResourceQueryParams {
  page?: number;
  limit?: number;
  courseId?: number;
}

interface FetchResourcesResponse {
  resources: Resource[];
  pagination: {
    total: number;
    currentPage: number;
    totalPages: number;
  };
}

interface ResourceState {
  resources: Record<number, Resource>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  total: number;
  currentPage: number;
  filters: {
    search: string;
    subject: SubjectType;
    type: ResourceType | "all";
  };
}

// Initial state
const initialState: ResourceState = {
  resources: {},
  status: "idle",
  error: null,
  filters: {
    search: "",
    subject: "All",
    type: "all",
  },
  total: 0,
  currentPage: 1,
};

// Thunks
export const fetchResources = createAsyncThunk<
  FetchResourcesResponse | null,
  ResourceQueryParams,
  { rejectValue: { message: string } }
>("resources/fetchResources", async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.get<FetchResourcesResponse>(
      `/resources`,
      { params }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to fetch resources"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to fetch resources",
    });
  }
});

export const uploadResource = createAsyncThunk<
  Resource,
  FormData,
  { rejectValue: { message: string } }
>("resources/createResource", async (formData, { rejectWithValue }) => {
  try {
    const response = await apiService.post<Resource>(
      `/resources/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    toast.success("Resource uploaded successfully");
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(
      axiosError.response?.data?.message || "Failed to create resource"
    );
    return rejectWithValue({
      message:
        axiosError.response?.data?.message || "Failed to create resource",
    });
  }
});

// Slice
const resourceSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },
    setSubjectFilter: (state, action: PayloadAction<SubjectType>) => {
      state.filters.subject = action.payload;
    },
    setTypeFilter: (state, action: PayloadAction<ResourceType | "all">) => {
      state.filters.type = action.payload;
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    resetResourceError(state) {
      state.error = null;
    },
    resetResourceStatus(state) {
      state.status = "idle";
    },
    invalidateCache(state) {
      state.resources = {};
      state.total = 0;
    },
  },
  extraReducers(builder) {
    builder
      // Fetch resources
      .addCase(fetchResources.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        if (action.payload) {
          const { resources, pagination } = action.payload;
          state.resources = resources.reduce(
            (acc, resource) => ({ ...acc, [resource.id]: resource }),
            {}
          );
          state.total = pagination.total;
          state.currentPage = pagination.currentPage;
          state.status = "succeeded";
        }
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch resources";
      })
      // Upload resource
      .addCase(uploadResource.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadResource.fulfilled, (state, action) => {
        state.resources[action.payload.id] = action.payload;
        state.status = "succeeded";
      })
      .addCase(uploadResource.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to create resource";
      });
  },
});

// Actions
export const {
  setCurrentPage,
  resetResourceError,
  invalidateCache,
  setSearchQuery,
  setSubjectFilter,
  setTypeFilter,
  resetResourceStatus
} = resourceSlice.actions;

// Selectors
export const selectResources = createSelector(
  [(state: { resources: ResourceState }) => state.resources.resources],
  (resources): Resource[] => Object.values(resources)
);

export const selectResourceById = createSelector(
  [
    (state: { resources: ResourceState }) => state.resources.resources,
    (_: any, id: number) => id,
  ],
  (resources, id): Resource | undefined => resources[id]
);

export const selectFilteredResources = (state: {
  resources: ResourceState;
}) => {
  const resources = Object.values(state.resources.resources);
  const { search, subject, type } = state.resources.filters;

  return resources.filter((resource) => {
    const matchesSearch =
      search === "" ||
      resource.title.toLowerCase().includes(search.toLowerCase()) ||
      resource.description.toLowerCase().includes(search.toLowerCase());

    const matchesSubject = subject === "All" || resource.subject === subject;
    const matchesType = type === "all" || resource.resourceType === type;

    return matchesSearch && matchesSubject && matchesType;
  });
};

// Export reducer
export default resourceSlice.reducer;
